import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment';
import t from 'react-translate';
import { range } from 'underscore';
import { AngularContext } from 'react-app';

// Redux
import { useSelector } from 'react-redux';
import { getUserTimezone } from 'redux/selectors/users';
import { MentorshipProgramSession } from 'redux/schemas/models/mentoring-program-sessions';

// Hooks
import { useFormContext } from 'react-hook-form';
import useDatePickerFocusHandler from 'shared/hooks/use-datepicker-focus-handler';
import useCalendarIntegration from 'athena/hooks/use-calendar-integration';

// Constants
import { tooltipZIndex } from 'athena/components/constants';
import { durationText } from 'shared/utils';

// Components
import NvDatePicker, { DatePickerType } from 'shared/components/inputs/nv-datepicker';
import NvDropdown, {
  NvDropdownOption,
  NvDropdownButtonStyle,
} from 'shared/components/inputs/nv-dropdown';
import MeetingPlanDetails from './meeting-plan-details';
import SchedulingWithAvailability from './scheduling-with-availability';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';

const MAX_WIDTH_TIMEZONE_TOOLTIP = 150;

type SessionScheduleRecurrenceProps = Pick<MentorshipProgramSession, 'duration'> & {
  isOptional: boolean;
  showMeetingPlan: boolean;
  allowPastDateSelection?: boolean;
};

const getTimezoneTranslation = (timezone) => {
  if (!timezone) return '';
  return t.TIMEZONE[timezone.translationKey]?.() ?? timezone.shortName;
};

const SessionScheduleRecurrence = (props: SessionScheduleRecurrenceProps) => {
  const {
    duration,
    isOptional = false,
    showMeetingPlan = false,
    allowPastDateSelection = true,
  } = props;
  const translations = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.EDIT_MODE;
  const durations = range(12).map((val) => (val + 1) * 30);
  const { setValue, watch } = useFormContext();
  const endsOn = watch('endsOn');
  const calendarHeight = 330;
  const [isSpaceAvailable, setIsSpaceAvailable] = useState(false);

  const { injectServices } = useContext(AngularContext);
  const [TimezonesService] = injectServices(['TimezonesService']);
  const currentUserTimezone = useSelector(getUserTimezone);
  const selectedTimezone = TimezonesService.timezones.find((tz) => tz.longName === currentUserTimezone);
  const formattedSelectedTimezone = selectedTimezone
    ? `${getTimezoneTranslation(selectedTimezone)} - ${selectedTimezone.offset.replace(/[()]/g, '')}`
    : 'UTC';

  const availabilityCalendarPlacement = isSpaceAvailable ? 'bottom-start' : 'top-start';

  const { isAuthenticated } = useCalendarIntegration();

  const datepickerRef = useRef(null);
  useDatePickerFocusHandler(datepickerRef);

  const durationItems = useMemo(() => (
    durations.map((d) => ({
      id: d,
      type: 'text',
      text: durationText(d),
      callback: () => setValue('duration', d, { shouldDirty: true }),
    })) as NvDropdownOption[]
  ), [setValue, durations]);

  const durationIndex = useMemo(() => {
    const index = durations.indexOf(duration);
    return index === -1 ? 0 : index;
  }, [duration, durations]);

  const checkSpace = () => {
    const suggestedTimesElement = document.getElementById('view-suggested-meeting-times');
    const buttonsRowElement = document.getElementById('session-buttons-row');

    if (suggestedTimesElement && buttonsRowElement) {
      const suggestedTimesRect = suggestedTimesElement.getBoundingClientRect();
      const buttonsRowRect = buttonsRowElement.getBoundingClientRect();
      const availableSpace = buttonsRowRect.top - suggestedTimesRect.bottom;

      setIsSpaceAvailable(availableSpace >= calendarHeight);
    }
  };

  useEffect(() => {
    checkSpace();
    window.addEventListener('resize', checkSpace);
    return () => {
      window.removeEventListener('resize', checkSpace);
    };
  }, []);

  return (
    <div className='recurrence-info pt-4'>
      {!isOptional && showMeetingPlan && (
        <div className='heading-4 pb-4'>
          {translations.RECURRENCE_TITLE()}
        </div>
      )}
      <div className='d-flex align-items-baseline session-date-time'>
        <div className='session-next-meeting pr-2'>
          <div className='session-time mb-1 d-flex align-items-center'>
            {(isOptional || !showMeetingPlan) ? translations.DATE_AND_TIME() : translations.NEXT_MEETING()}
            <NvTooltip
              text={formattedSelectedTimezone}
              zIndex={tooltipZIndex}
              maxWidth={MAX_WIDTH_TIMEZONE_TOOLTIP}
            >
              <div>
                <NvIcon
                  icon='info'
                  size='xss-smallest'
                  className='mx-1'
                />
              </div>
            </NvTooltip>
          </div>
          <NvDatePicker
            withForm
            ref={datepickerRef}
            type={DatePickerType.DATETIME}
            name='scheduledDate'
            placement='top-start'
            highlightDates={[moment().startOf('day')]}
            min={(allowPastDateSelection && !showMeetingPlan) ? undefined : moment()}
            max={endsOn ? moment(endsOn).subtract(1, 'day') : undefined}
            hideKeyboardOnMobile
            required
          />
          {isAuthenticated && (
            <SchedulingWithAvailability
              placement={availabilityCalendarPlacement}
            />
          )}
        </div>
        <div className='session-timezone-duration d-flex align-items-start'>
          <div className='session-duration'>
            <div className='session-time mb-1'>{translations.DURATION()}</div>
            <NvDropdown
              initialIndex={durationIndex}
              showSelectedIndicator
              items={durationItems}
              buttonStyle={NvDropdownButtonStyle.FORM_SMALL}
              menuClassName='w-100'
              minWidth={120}
            />
          </div>
        </div>
      </div>
      {showMeetingPlan && <MeetingPlanDetails isOptional={isOptional} />}
    </div>
  );
};

export default SessionScheduleRecurrence;
